import { ReactNode, useContext, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import classnames from 'classnames';
import { AuthContext, User } from 'Auth';
import { Disclosure } from '@headlessui/react';
import Breadcrumbs, { BreadcrumbItem } from 'components/Breadcrumbs';

const navigationsArtist = [
    { key: "/profile", name: "Profile Picture", br: "Profile" },
    // { key: "/profile/reset-password", name: "Reset password" },
    { key: "/profile/setting-studio", name: "Studio" },
    { key: "/profile/setting-artist", name: "Private Profile" },
    { key: "/profile/about", name: "Publish Profile" },
    { key: "/profile/slug", name: "Profile Url" },
];

const navigationsGallery = [
    { key: "/profile", name: "Profile Picture", br: "Profile" },
    // { key: "/profile/reset-password", name: "Reset password" },
    { key: "/profile/setting-studio", name: "Studio" },
    { key: "/profile/setting-gallery", name: "Private Profile" },
    { key: "/profile/about", name: "Publish Profile" },
    { key: "/profile/slug", name: "Profile Url" },
];

interface PropsType {
    children: ReactNode,
}

export default function ContainerProfile({ children }: PropsType) {
    const navigate = useNavigate();
    const location = useLocation();

    const user: User = useContext(AuthContext);

    const path = location.pathname;

    const navigations: any[] = useMemo(() => {
        switch (user.usertype) {
            case "gallery":
                return navigationsGallery;
            default:
                return navigationsArtist;
        }
    }, [user?.usertype]);


    const breadcrumbs: BreadcrumbItem[] = useMemo((() => {
        let result: BreadcrumbItem[] = [];

        const p: string[] = path.split("/");
        let src = "";

        for(var key of p) {
            if (key.length > 0) {
                const newSrc = src + "/" + key;
                const navigation = navigations.find((n) => n.key === newSrc);
                if (navigation) {
                    result = [...result, {
                        title: navigation?.br ?? navigation?.name,
                        src: newSrc,
                    }];
                }
                src = newSrc;
            }
        }

        return result;
    }), [path, navigations]);
    
    return (
        <div className='pt-6 md:pt-10'>
            <div className='md:block hidden'>
                <Breadcrumbs breadcrumbs={breadcrumbs} />
            </div>
            <div className='flex flex-col md:flex-row items-stretch pb-6 pt-0 gap-4 md:gap-6 lg:gap-10 md:pb-10 md:pt-6'>
                <div className='hidden md:flex md:flex-col md:gap-1 md:w-48 lg:w-64'>
                    {navigations.map((navigation) => {
                        const selected: boolean = navigation.key === path;
                        return (
                            <button
                                className={
                                    classnames(
                                        "px-4 py-2.5 border-s-4 text-sm text-start rounded",
                                        {
                                            "text-red-700 border-red-700 font-medium bg-red-50 hover:bg-red-100": selected,
                                            "border-transparent font-normal text-gray-700 hover:bg-gray-100 hover:text-gray-900": !selected,
                                        }
                                    )
                                }
                                key={navigation.key}
                                onClick={!selected ? () => navigate(navigation.key) : () => { }}
                            >
                                {navigation.name}
                            </button>
                        );
                    })}
                </div>
                <div className='md:hidden block'>
                    <Disclosure as="nav">
                        {({ open }) => (
                            <div className={
                                classnames(
                                    "",
                                    {
                                        "border-b": open,
                                    }
                                )
                            }>
                                <div className='flex justify-between gap-3'>
                                    <Breadcrumbs breadcrumbs={breadcrumbs} />
                                    <Disclosure.Button className="inline-flex items-center justify-center rounded-md shad bg-gray-100 p-2 hover:bg-gray-200 focus:outline-none focus:ring-0 focus:ring-slate-500 focus:ring-offset-2">
                                        <span className="sr-only">Open main menu</span>
                                        {open ? (
                                            <svg className="w-5 h-5 text-gray-800 dark:text-white" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18 18 6m0 12L6 6" />
                                            </svg>
                                        ) : (
                                            <svg className="w-5 h-5 text-gray-800 dark:text-white" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <path stroke="currentColor" strokeLinecap="round" strokeWidth="2" d="M5 7h14M5 12h14M5 17h14" />
                                            </svg>
                                        )}
                                    </Disclosure.Button>
                                </div>
                                <Disclosure.Panel className="block md:hidden mt-4">
                                    <div className="space-y-1 pt-2 pb-3">
                                        {navigations.map((item) => (
                                            <Disclosure.Button
                                                key={item.name}
                                                as="button"
                                                onClick={!(path === item.key) ? () => navigate(item.key) : () => { }}
                                                className={classnames(
                                                    "px-4 py-2.5 w-full border-s-4 text-sm text-start rounded",
                                                    {
                                                        "text-red-700 border-red-700 font-medium bg-red-50 hover:bg-red-100": item.key === path,
                                                        "border-transparent font-normal text-gray-700 hover:bg-gray-100 hover:text-gray-900": !(item.key === path),
                                                    }
                                                )}
                                                aria-current={path === item.key ? "page" : undefined}
                                            >
                                                {item.name}
                                            </Disclosure.Button>
                                        ))}
                                    </div>
                                </Disclosure.Panel>
                            </div>
                        )}
                    </Disclosure>

                </div>
                <div className='hidden md:block md:w-px md:bg-gray-200' />
                <div className='md:flex-1'>
                    {children}
                </div>
            </div>
        </div>
    );
}